// src/AdminPages/YtVideos/AddYtVideos.jsx

import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, FaTrashAlt, FaTimes, FaSearch, 
    FaChevronDown, FaChevronUp, FaPlus, 
    FaYoutube, FaSave, FaPlayCircle, FaCalendarAlt
} from 'react-icons/fa';
import './AddYtVideos.css';
// --- CHANGE 1: Import our new global axios instance ---
import axiosInstance from '../../api/axiosInstance'; 

// --- HELPER FUNCTIONS (No changes needed here) ---
const getYoutubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};
const getThumbnail = (url) => {
    const id = getYoutubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : 'https://via.placeholder.com/300x169/000000/ffffff?text=No+Video';
};
const getEmbedUrl = (url) => {
    const id = getYoutubeId(url);
    return id ? `https://www.youtube.com/embed/${id}` : '';
};
const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';


// --- MODAL COMPONENT (No changes needed here) ---
const VideoFormModal = ({ isOpen, onClose, data, mode, onSave }) => {
    const [formData, setFormData] = useState({ title: '', link: '', isActive: true });
    
    useEffect(() => {
        if (data && mode === 'edit') setFormData(data);
        else setFormData({ title: '', link: '', isActive: true });
    }, [data, mode, isOpen]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = () => {
        if (!formData.title) return Swal.fire('Error', 'Please enter a video title', 'error');
        if (!formData.link || !getYoutubeId(formData.link)) return Swal.fire('Error', 'Please enter a valid YouTube URL', 'error');
        onSave(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="add-yt-video-of-sk-college-modal-overlay">
            <div className="add-yt-video-of-sk-college-modal-content" onClick={e => e.stopPropagation()}>
                <div className="add-yt-video-of-sk-college-modal-header">
                    <h2>{mode === 'edit' ? 'Edit Video' : 'Add Video'}</h2>
                    <button className="add-yt-video-of-sk-college-modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="add-yt-video-of-sk-college-modal-body">
                    {/* All your JSX for the modal remains exactly the same */}
                    <div className="add-yt-video-of-sk-college-form-group"><label>Video Title</label><input type="text" name="title" value={formData.title} onChange={handleChange} className="add-yt-video-of-sk-college-input" placeholder="Enter video title..."/></div>
                    <div className="add-yt-video-of-sk-college-form-group"><label>YouTube Link / Embed Code</label><input type="text" name="link" value={formData.link} onChange={handleChange} className="add-yt-video-of-sk-college-input" placeholder="e.g. https://youtu.be/..."/><small className="add-yt-video-of-sk-college-hint">Supports Standard URLs and Share Links.</small></div>
                    {getYoutubeId(formData.link) && (<div className="add-yt-video-of-sk-college-preview-container"><label>Preview</label><div className="add-yt-video-of-sk-college-iframe-wrapper"><iframe src={getEmbedUrl(formData.link)} title="YouTube Preview" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe></div></div>)}
                    {mode === 'edit' && (<div className="add-yt-video-of-sk-college-dates-row"><div className="add-yt-video-of-sk-college-meta-item"><FaCalendarAlt /> Created: {formatDate(formData.createdAt)}</div><div className="add-yt-video-of-sk-college-meta-item"><FaCalendarAlt /> Updated: {formatDate(formData.updatedAt)}</div></div>)}
                </div>
                <div className="add-yt-video-of-sk-college-modal-footer">
                    <button className="add-yt-video-of-sk-college-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="add-yt-video-of-sk-college-btn-save" onClick={handleSubmit}><FaSave style={{marginRight: '5px'}}/> Save Video</button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const AddYtVideos = () => {
    const [videos, setVideos] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); 
    const [selectedItem, setSelectedItem] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);

    // --- CHANGE 2: The security guard useEffect is no longer needed.
    // The axios interceptor handles this automatically on EVERY API call.

    // --- FETCH DATA ---
    const fetchVideos = async () => {
        try {
            // --- CHANGE 3: Use axiosInstance. No headers or token needed here. ---
            const response = await axiosInstance.get('/api/ytVideos');
            
            if (Array.isArray(response.data)) {
                setVideos(response.data);
            } else {
                setVideos([]); 
            }
        } catch (error) {
            // The interceptor handles 401. This catches other errors (e.g., server offline).
            if (error.response?.status !== 401) {
                console.error("Error fetching videos:", error);
                setVideos([]); 
                Swal.fire('Error', error.response?.data?.message || 'Failed to fetch videos', 'error');
            }
        }
    };

    useEffect(() => {
        fetchVideos();
    }, []);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    
    // Filtering and Pagination Logic (No changes needed)
    const filteredData = useMemo(() => {
        if (!Array.isArray(videos)) return [];
        return videos.filter(item => (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())));
    }, [videos, searchTerm]);

    const totalResults = filteredData.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    const startResult = totalResults === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endResult = Math.min(currentPage * itemsPerPage, totalResults);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    // --- CRUD Handlers ---
    const handleSave = async (data) => {
        try {
            if (modalMode === 'edit') {
                // --- CHANGE 4: Use axiosInstance for PUT request ---
                await axiosInstance.put(`/api/ytVideos/${data._id}`, data);
                Swal.fire('Updated!', 'Video updated successfully.', 'success');
            } else {
                // --- CHANGE 5: Use axiosInstance for POST request ---
                await axiosInstance.post('/api/ytVideos', data);
                Swal.fire('Added!', 'Video added successfully.', 'success');
            }
            fetchVideos();
            setIsModalOpen(false);
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error(error);
                Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
            }
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({ title: 'Delete Video?', text: 'Cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e11d48' }).then(async res => {
            if (res.isConfirmed) {
                try {
                    // --- CHANGE 6: Use axiosInstance for DELETE request ---
                    await axiosInstance.delete(`/api/ytVideos/${id}`);
                    Swal.fire('Deleted', '', 'success');
                    fetchVideos(); // Fetch fresh data after deleting
                } catch (error) {
                    if (error.response?.status !== 401) {
                        Swal.fire('Error', 'Failed to delete', 'error');
                    }
                }
            }
        });
    };

    const toggleActive = async (item) => {
        try {
            const updatedData = { ...item, isActive: !item.isActive };
            // --- CHANGE 7: Use axiosInstance for PUT request (toggle) ---
            await axiosInstance.put(`/api/ytVideos/${item._id}`, updatedData);
            fetchVideos(); // Re-fetch to confirm the change from the server
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error("Error toggling active status", error);
                Swal.fire('Error', 'Failed to update status', 'error');
            }
        }
    };

    const openModal = (mode, item = null) => {
        setModalMode(mode);
        setSelectedItem(item);
        setIsModalOpen(true);
    };

    // --- YOUR JSX REMAINS UNCHANGED ---
    return (
        <div className="add-yt-video-of-sk-college-wrapper">
            <div className="add-yt-video-of-sk-college-container">
                <VideoFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data={selectedItem} mode={modalMode} onSave={handleSave} />
                <div className="add-yt-video-of-sk-college-header"><div className="add-yt-video-of-sk-college-header-title"><h1>Video Gallery Management</h1></div><button className="add-yt-video-of-sk-college-add-btn" onClick={() => openModal('add')}><FaPlus /> Add Video</button></div>
                <div className="add-yt-video-of-sk-college-controls-row"><div className="add-yt-video-of-sk-college-search-wrapper"><FaSearch className="add-yt-video-of-sk-college-search-icon" /><input type="text" placeholder="Search video title..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="add-yt-video-of-sk-college-search-input" /></div><div className="add-yt-video-of-sk-college-filter-group"><label>Show:</label><select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="add-yt-video-of-sk-college-select"><option value="10">10</option><option value="20">20</option><option value="50">50</option></select></div></div>
                <div className="add-yt-video-of-sk-college-table-card"><div className="add-yt-video-of-sk-college-table-wrapper"><table className="add-yt-video-of-sk-college-table">
                    <thead><tr><th className="add-yt-video-of-sk-college-col-sno">S.No</th><th className="add-yt-video-of-sk-college-col-video">Video Information</th><th className="add-yt-video-of-sk-college-col-active">Active</th><th className="add-yt-video-of-sk-college-col-actions">Action</th></tr></thead>
                    <tbody>
                        {paginatedData.map((item, index) => (
                            <React.Fragment key={item._id}>
                                <tr className="add-yt-video-of-sk-college-row">
                                    <td className="add-yt-video-of-sk-college-col-sno">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td className="add-yt-video-of-sk-college-col-video"><div className="add-yt-video-of-sk-college-info-wrapper"><div className="add-yt-video-of-sk-college-thumb-box"><img src={getThumbnail(item.link)} alt="thumb" /><FaYoutube className="add-yt-video-of-sk-college-play-icon"/></div><div className="add-yt-video-of-sk-college-text-box"><span className="video-title">{item.title}</span><a href={item.link} target="_blank" rel="noopener noreferrer" className="video-link">Watch on YouTube</a></div><button className="add-yt-video-of-sk-college-mobile-toggle-btn" onClick={() => toggleRow(item._id)}>{expandedRow === item._id ? <FaChevronUp /> : <FaChevronDown />}</button></div></td>
                                    <td className="add-yt-video-of-sk-college-col-active"><div className="add-yt-video-of-sk-college-switch" onClick={() => toggleActive(item)}><div className={`add-yt-video-of-sk-college-switch-handle ${item.isActive ? 'active' : ''}`}></div></div></td>
                                    <td className="add-yt-video-of-sk-college-col-actions"><div className="add-yt-video-of-sk-college-action-icons"><button className="add-yt-video-of-sk-college-icon-btn edit" title="Edit" onClick={() => openModal('edit', item)}><FaEdit /></button><button className="add-yt-video-of-sk-college-icon-btn delete" title="Delete" onClick={() => handleDelete(item._id)}><FaTrashAlt /></button></div></td>
                                </tr>
                                {expandedRow === item._id && (
                                    <tr className="add-yt-video-of-sk-college-details-row"><td colSpan="4"><div className="add-yt-video-of-sk-college-details-content"><div className="add-yt-video-of-sk-college-details-grid">
                                        <div className="add-yt-video-of-sk-college-detail-card dc-video"><div className="add-yt-video-of-sk-college-detail-label"><FaPlayCircle style={{marginRight: '6px'}}/> Video Preview</div><div className="add-yt-video-of-sk-college-iframe-wrapper"><iframe src={getEmbedUrl(item.link)} title={item.title} frameBorder="0" allowFullScreen></iframe></div></div>
                                        <div className="add-yt-video-of-sk-college-detail-card dc-meta"><div className="add-yt-video-of-sk-college-detail-label">Details</div><div className="add-yt-video-of-sk-college-meta-list"><span><strong>Title:</strong> {item.title}</span><span><strong>Created:</strong> {formatDate(item.createdAt)}</span><span><strong>Updated:</strong> {formatDate(item.updatedAt)}</span></div></div>
                                        <div className="add-yt-video-of-sk-college-detail-card dc-active"><div className="add-yt-video-of-sk-college-detail-label">Active</div><div className="add-yt-video-of-sk-college-switch" onClick={() => toggleActive(item)}><div className={`add-yt-video-of-sk-college-switch-handle ${item.isActive ? 'active' : ''}`}></div></div></div>
                                        <div className="add-yt-video-of-sk-college-detail-card dc-actions"><div className="add-yt-video-of-sk-college-detail-label">Actions</div><div className="add-yt-video-of-sk-college-action-icons"><button className="add-yt-video-of-sk-college-icon-btn edit" onClick={() => openModal('edit', item)}><FaEdit /></button><button className="add-yt-video-of-sk-college-icon-btn delete" onClick={() => handleDelete(item._id)}><FaTrashAlt /></button></div></div>
                                    </div></div></td></tr>
                                )}
                            </React.Fragment>
                        ))}
                        {paginatedData.length === 0 && <tr><td colSpan="4" className="add-yt-video-of-sk-college-no-data">No videos found.</td></tr>}
                    </tbody>
                </table></div></div>
                <div className="add-yt-video-of-sk-college-pagination-container"><div className="add-yt-video-of-sk-college-pagination-info">Showing {startResult} to {endResult} of {totalResults} results</div><div className="add-yt-video-of-sk-college-pagination-controls"><button className="add-yt-video-of-sk-college-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button><span className="add-yt-video-of-sk-college-page-count">Page {currentPage} of {totalPages || 1}</span><button className="add-yt-video-of-sk-college-page-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>Next</button></div></div>
            </div>
        </div>
    );
};

export default AddYtVideos;